import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { UserRole } from "../../types";
import { useTranslation } from "react-i18next";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireWorkflowManager?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAdmin = false,
  requireWorkflowManager = false,
}) => {
  const { t } = useTranslation(["auth"]);
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (
    requireAdmin &&
    user.role !== UserRole.ADMIN &&
    user.role !== UserRole.MANAGER
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t("auth:accessDenied")}
          </h2>
          <p className="text-gray-600">{t("auth:accessDeniedMessage")}</p>
        </div>
      </div>
    );
  }

  if (
    requireWorkflowManager &&
    user.role !== UserRole.ADMIN &&
    user.role !== UserRole.MANAGER &&
    user.role !== UserRole.PROJECT_MANAGER
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t("auth:accessDenied")}
          </h2>
          <p className="text-gray-600">{t("auth:accessDeniedMessage")}</p>
        </div>
      </div>
    );
  }

  if (user.role === UserRole.VISITOR) {
    const visitorPath = location.pathname;
    const canAccessProjectList = visitorPath === "/project-instances";
    const canAccessArchivedProjectList = visitorPath === "/archived-projects";
    const canAccessProjectDetail = /^\/project-instances\/\d+$/.test(visitorPath);

    if (
      !canAccessProjectList &&
      !canAccessArchivedProjectList &&
      !canAccessProjectDetail
    ) {
      return <Navigate to="/project-instances" replace />;
    }
  }

  return <>{children}</>;
};
